import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Phone } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { allCities } from '../data/cityData';

export const CitiesWeServe = () => {
  return (
    <div data-testid="cities-we-serve-page">
      <Helmet>
        <title>Cities We Serve | A-Quality Pool Service | Central & Gulf Coast Florida</title>
        <meta name="description" content="A-Quality Pool Service provides pool cleaning, repair, and maintenance in Tampa, Wesley Chapel, Zephyrhills, Brandon, Riverview, Lutz, Land O' Lakes, Odessa, Seffner, and Temple Terrace, FL." />
        <link rel="canonical" href="https://a-qualitypools.net/cities-we-serve" />
      </Helmet>
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <MapPin className="h-5 w-5 text-sky-400 mr-2" />
              <span className="text-white font-medium">Central & Gulf Coast Florida</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Cities We Serve
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              A-Quality Pool Service proudly provides premium pool cleaning, repair, maintenance, and renovation services across the Central and Gulf Coast Florida area.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {allCities.map((city, i) => (
                <Link key={city.slug} to={`/pool-service/${city.slug}`} className="group">
                  <Card className="h-full border-slate-200 hover:border-sky-500 transition-all hover:shadow-lg group-hover:-translate-y-1 duration-200">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-sky-50 rounded-lg group-hover:bg-sky-100 transition-colors">
                          <MapPin className="h-5 w-5 text-sky-600" />
                        </div>
                        <div>
                          <h2 className="text-lg font-bold text-slate-900 group-hover:text-sky-600 transition-colors">
                            {city.name}
                          </h2>
                          <p className="text-xs text-slate-500">{city.county}</p>
                        </div>
                      </div>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">{city.description}</p>
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {city.services.slice(0, 3).map((s, j) => (
                          <span key={j} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            {s.title.replace(city.name + ' ', '')}
                          </span>
                        ))}
                      </div>
                      <span className="inline-flex items-center text-sky-600 text-sm font-medium group-hover:gap-2 transition-all">
                        View {city.name} Services <ArrowRight className="h-4 w-4 ml-1" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="pt-16 pb-32 bg-sky-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Don't See Your City?
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              We serve many more areas across Central and Gulf Coast Florida. Call us to find out if we service your location.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-sky-700 hover:bg-slate-100 font-semibold px-8">
                  Contact Us
                </Button>
              </Link>
              <a href="tel:813-453-5988">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8">
                  <Phone className="h-4 w-4 mr-2" /> 813-453-5988
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CitiesWeServe;
